import type { ComponentType } from 'react';
import { cn } from '@/lib/cn';

export interface SegmentedOption<T extends string> {
    value: T;
    label: string;
    Icon?: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
}

interface Props<T extends string> {
    legend: string;
    /** Oculta la leyenda visualmente; sigue nombrando al grupo para lectores de pantalla. */
    hideLegend?: boolean;
    name: string;
    value: T;
    options: SegmentedOption<T>[];
    onChange: (value: T) => void;
}

/** Grupo de opciones excluyentes con estilo segmentado, sobre radios nativos. */
export function SegmentedControl<T extends string>({
    legend,
    hideLegend = false,
    name,
    value,
    options,
    onChange,
}: Props<T>) {
    return (
        <fieldset className="space-y-2">
            <legend className={cn('text-sm font-medium', hideLegend && 'sr-only')}>{legend}</legend>
            <div className="inline-flex rounded-lg border border-border bg-muted/40 p-1">
                {options.map(({ value: option, label, Icon }) => {
                    const id = `${name}-${option || 'all'}`;
                    const checked = value === option;
                    return (
                        <label
                            key={option}
                            htmlFor={id}
                            className={cn(
                                'inline-flex cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring',
                                checked
                                    ? 'bg-background font-medium text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                        >
                            <input
                                id={id}
                                type="radio"
                                name={name}
                                value={option}
                                checked={checked}
                                onChange={() => {
                                    onChange(option);
                                }}
                                className="sr-only"
                            />
                            {Icon && <Icon className="size-4" aria-hidden />}
                            {label}
                        </label>
                    );
                })}
            </div>
        </fieldset>
    );
}
